/**
 * PATH: packages/email-hub/src/feature/linked-emails/api.ts
 * FILE_ID: EMAILHUB-LINKED-EMAILS-API-001
 * PURPOSE: Pure I→O helpers that wrap the master backend's email
 *          endpoints for the Linked Emails panel.
 *
 * WHY: Convergence B2 lifted these from the tenant fork
 *      (apps/newmark features/quotes LinkedEmailsPanel/api.ts) so every
 *      tenant + the master SPA share one contract with /api/emails/.
 *
 *      Errors are surfaced (thrown) from helpers that the panel must
 *      react to — `searchMailbox`, `linkEmailToJob`, `linkEmailsToJob`
 *      — so the UI can render a "search failed" / toast as appropriate.
 *      Convenience reads (`fetchLinkedEmails`, `fetchRecentEmails`)
 *      swallow errors and return [] because the panel renders the
 *      empty state in either case and we don't want a flaky inbox to
 *      hide the linked list.
 *
 * FLOW:
 *   ┌────────────────┐    ┌───────────────────────┐    ┌──────────────┐
 *   │ /api/emails/…  │ →  │ quotechJson (master)  │ →  │ typed rows   │
 *   └────────────────┘    └───────────────────────┘    └──────────────┘
 * DEPENDENCIES:
 *   - ../../_internal/services/quotechApi: canonical fetch client
 *     (re-export of @quotech/tenant-shared/services/quotechApi).
 */

import { quotechJson } from '../../_internal/services/quotechApi'

import type { LinkedEmailRow, ListEnvelope } from './types'

const RECENT_PAGE_SIZE = 50
const SEARCH_PAGE_SIZE = 20

export async function fetchLinkedEmails(jobId: number): Promise<LinkedEmailRow[]> {
  try {
    const { data } = await quotechJson<ListEnvelope | LinkedEmailRow[]>(
      `/api/emails/?job_id=${jobId}&page_size=50`,
    )
    return Array.isArray(data) ? data : (data?.results ?? [])
  } catch {
    return []
  }
}

/**
 * Fetch the user's most recent mailbox messages so the operator can
 * browse-and-link without typing a search. Quietly returns [] on
 * transport error — the recent inbox is a convenience surface, not a
 * blocking one, so a flaky backend mustn't hide the linked-list above.
 */
export async function fetchRecentEmails(): Promise<LinkedEmailRow[]> {
  try {
    const params = new URLSearchParams()
    params.set('page_size', String(RECENT_PAGE_SIZE))
    params.set('ordering', '-received_at')
    const { data } = await quotechJson<ListEnvelope | LinkedEmailRow[]>(
      `/api/emails/?${params.toString()}`,
    )
    return Array.isArray(data) ? data : (data?.results ?? [])
  } catch {
    return []
  }
}

export async function searchMailbox(query: string): Promise<LinkedEmailRow[]> {
  const params = new URLSearchParams()
  params.set('search', query)
  params.set('page_size', String(SEARCH_PAGE_SIZE))
  const { data } = await quotechJson<ListEnvelope | LinkedEmailRow[]>(
    `/api/emails/?${params.toString()}`,
  )
  return Array.isArray(data) ? data : (data?.results ?? [])
}

export async function linkEmailToJob(emailId: string, jobId: number): Promise<void> {
  await linkEmailsToJob([emailId], jobId)
}

/**
 * Bulk-link variant. The master backend's `/api/emails/bulk/` endpoint
 * already accepts arrays — this exposes that capability so the operator
 * can pick several rows from the inbox and attach them in one round-trip.
 * No-ops on an empty array so callers don't need to guard.
 */
export async function linkEmailsToJob(
  emailIds: string[],
  jobId: number,
): Promise<void> {
  if (emailIds.length === 0) return
  await quotechJson(`/api/emails/bulk/`, {
    method: 'POST',
    body: { email_ids: emailIds, action: 'link_job', job_id: jobId },
  })
}
